import {
  convertDateToYYYYMMDDFormat,
  CustomDateRangePicker,
  NoDataComponent,
  PageWrapper,
  SimpleTableLoadingSkeleton,
  STATUSPAGE,
  TableVirtualized,
} from '@repo/ui';
import { useFormik } from 'formik';
import noData from 'presentation/app/assets/images/no-task.png';
import { makeActivitiesSummarizedByTaskColumns } from 'presentation/app/modules/Reports/common/columns/activities/activities-summarized-by-task-columns';
import { ReportsGeneralFilters } from 'presentation/app/modules/Reports/components/reports-general-filters';
import { useActivitiesSummaryByTask } from 'presentation/app/modules/Reports/hooks/useActivitiesSummaryByTask';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { Button, Card, Col, Input, Label, Row, Spinner } from 'reactstrap';
import * as Yup from 'yup';

const ActivitiesSummaryByTask = () => {
  const {
    loading,
    hasFetchedData,
    selectedSubmitBtn,
    requestHandleActivitiesSummaryByTask,
    setSelectedSubmitBtn,
    requestHandleActivitiesSummaryByTaskJson,
  } = useActivitiesSummaryByTask();

  const { activitiesByTask } = useAppSelector(
    (state) => state.activitiesSummaryByTask,
  );

  function getFirstDayOfMonth() {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }

  function getCurrentDate() {
    return new Date();
  }

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      unit: null,
      groups: [],
      entity: null,
      course: null,
      semester: null,
      ends: convertDateToYYYYMMDDFormat(getCurrentDate()),
      starts: convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
    },

    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      groups: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      starts: Yup.date().required('Campo é obrigatório!'),
      ends: Yup.date().required('Campo é obrigatório!'),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        entity: values.entity?.value,
        course: values.course?.value,
        semester: values.semester?.value,
        curricularUnit: values.unit?.value,
        groups: values.groups.map((group) => group.value),
        ends: values.ends,
        starts: values.starts,
      };

      switch (selectedSubmitBtn) {
        case 'export-pdf':
          requestHandleActivitiesSummaryByTask({
            ...valuesToSubmit,
            reportType: '0',
          });
          break;
        case 'export-excel':
          requestHandleActivitiesSummaryByTask({
            ...valuesToSubmit,
            reportType: '1',
          });
          break;
        case 'export-json':
          requestHandleActivitiesSummaryByTaskJson({
            ...valuesToSubmit,
            reportType: '2',
          });
          break;
        default:
          break;
      }
    },
  });

  const formattedData = activitiesByTask?.map((register, idx) => ({
    id: idx,
    ...register,
  }));

  return (
    <PageWrapper title="Resumido">
      <div className="mb-4">
        <h4>Resumido</h4>
        <p className="text-muted mb-0">
          Acompanhe o resumo de atividades agrupadas por tarefa.
        </p>
      </div>

      <Card>
        <ReportsGeneralFilters
          validation={validation}
          inputsChildren={
            <Col sm={12} md={6} lg={3} className="mb-3">
              <Row>
                <Label htmlFor="initial-date" className="form-label">
                  Período
                </Label>
                <CustomDateRangePicker
                  placement="auto"
                  onDateChange={(start, ends) => {
                    validation.setFieldValue(
                      'starts',
                      convertDateToYYYYMMDDFormat(start),
                    );
                    validation.setFieldValue(
                      'ends',
                      convertDateToYYYYMMDDFormat(ends),
                    );
                  }}
                />
              </Row>
            </Col>
          }
          buttonsChildren={
            <div className="d-flex gap-2 justify-content-end align-content-end">
              <Button
                onClick={() => {
                  setSelectedSubmitBtn('export-json');
                }}
                color="primary"
                type="submit"
                id="export-json"
                disabled={loading === STATUSPAGE.LOADING && true}
              >
                {selectedSubmitBtn === 'export-json' &&
                loading === STATUSPAGE.LOADING ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Visualizar
                    <i className="ri-computer-line mx-2"></i>
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  setSelectedSubmitBtn('export-pdf');
                }}
                color="primary"
                type="submit"
                className="btn btn-light waves-effect waves-light"
                id={'save_button'}
                disabled={loading === STATUSPAGE.LOADING && true}
              >
                {selectedSubmitBtn === 'export-pdf' &&
                loading === STATUSPAGE.LOADING ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Exportar para PDF
                    <i className="las la-file-pdf mx-2"></i>
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  setSelectedSubmitBtn('export-excel');
                }}
                color="primary"
                type="submit"
                id={'save_button'}
                className="btn btn-light waves-effect waves-light"
                disabled={loading === STATUSPAGE.LOADING && true}
              >
                {selectedSubmitBtn === 'export-excel' &&
                loading === STATUSPAGE.LOADING ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Exportar para Excel
                    <i className="ms-2 ri-file-excel-2-line"></i>
                  </>
                )}
              </Button>
            </div>
          }
        />
        {activitiesByTask?.length === 0 &&
          hasFetchedData &&
          loading === STATUSPAGE.DONE && (
            <div className="p-4">
              <NoDataComponent src={noData} />
            </div>
          )}

        {loading === STATUSPAGE.LOADING && <SimpleTableLoadingSkeleton />}

        {loading === STATUSPAGE.DONE && activitiesByTask?.length > 0 && (
          <TableVirtualized
            columns={makeActivitiesSummarizedByTaskColumns()}
            data={formattedData}
          />
        )}
      </Card>
    </PageWrapper>
  );
};

export default ActivitiesSummaryByTask;
